import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { LogOut, Shield, Calendar, DollarSign, Clock, Tag, ChevronRight, FileText, Palette } from 'lucide-react';

const API_URL = "";

export const MemberHistory = () => {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  
  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/members/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user && token) {
      fetchRegistrations();
    }
  }, [user, token]);

  const fetchRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registrations/by-email/${encodeURIComponent(user.email)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Get registration type badge color and icon
  const getTypeBadge = (type) => {
    switch (type) {
      case 'workshop':
        return { color: 'bg-blue-100 text-blue-800', icon: Palette, label: 'Workshop' };
      case 'membership':
        return { color: 'bg-cafa-red-100 text-cafa-red-900', icon: Tag, label: 'Membership' };
      case 'open_session':
        return { color: 'bg-green-100 text-green-800', icon: Calendar, label: 'Open Session' };
      case 'donation':
        return { color: 'bg-purple-100 text-purple-800', icon: DollarSign, label: 'Donation' };
      default:
        return { color: 'bg-stone-100 text-stone-800', icon: FileText, label: type };
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { color: 'bg-green-100 text-green-800', label: 'Completed' };
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' };
      case 'cancelled':
        return { color: 'bg-red-100 text-red-800', label: 'Cancelled' };
      default:
        return { color: 'bg-stone-100 text-stone-800', label: status };
    }
  };

  // Filter registrations
  const filteredRegistrations = registrations.filter(reg => {
    if (filter === 'all') return true;
    return reg.registration_type === filter;
  });

  // Get unique registration types for filter
  const registrationTypes = ['all', ...new Set(registrations.map(r => r.registration_type))];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="member-history-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-8 border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif text-stone-900">Member Area</h1>
              <p className="text-stone-600 mt-1">Welcome, {user?.first_name}!</p>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {isAdmin && (
                <Link to="/admin/content">
                  <Button variant="ghost" className="text-cafa-red">
                    <Shield size={16} className="mr-1" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/members/directory">
                <Button variant="ghost" className="text-stone-600">
                  Member Directory
                </Button>
              </Link>
              <Link to="/members/profile">
                <Button variant="ghost" className="text-stone-600">
                  My Profile
                </Button>
              </Link>
              <Link to="/members/history">
                <Button variant="ghost" className="text-cafa-red">
                  My History
                </Button>
              </Link>
              <Link to="/members/gallery" data-testid="member-history-gallery-link">
                <Button variant="ghost" className="text-stone-600">Gallery Applications</Button>
              </Link>
              <Button variant="ghost" onClick={handleLogout} className="text-stone-600">
                <LogOut size={18} className="mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Registration History */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-stone-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle className="text-2xl font-serif">Registration History</CardTitle>
                
                {/* Filter Tabs */}
                <div className="flex gap-2 flex-wrap">
                  {registrationTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setFilter(type)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors capitalize ${
                        filter === type
                          ? 'bg-cafa-red text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                      data-testid={`filter-${type}`}
                    >
                      {type === 'all' ? 'All' : type.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cafa-red"></div>
                </div>
              ) : filteredRegistrations.length === 0 ? (
                <div className="text-center py-12">
                  <FileText size={48} className="mx-auto text-stone-300 mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Registrations Found</h3>
                  <p className="text-stone-600 mb-6">
                    {filter === 'all' 
                      ? "You haven't registered for any workshops or classes yet."
                      : `No ${filter.replace('_', ' ')} registrations found.`
                    }
                  </p>
                  <div className="flex justify-center gap-4">
                    <Link to="/workshops">
                      <Button className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                        Browse Workshops
                      </Button>
                    </Link>
                    <Link to="/classes/adult">
                      <Button variant="outline" className="border-cafa-red text-cafa-red hover:bg-cafa-red-50">
                        View Classes
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRegistrations.map((registration) => {
                    const typeBadge = getTypeBadge(registration.registration_type);
                    const statusBadge = getStatusBadge(registration.status);
                    const TypeIcon = typeBadge.icon;
                    
                    return (
                      <div 
                        key={registration.id}
                        className="border border-stone-200 rounded-lg p-4 hover:border-cafa-blue-200 hover:bg-cafa-red-50/30 transition-colors"
                        data-testid={`registration-${registration.id}`}
                      >
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                          {/* Left side - Registration details */}
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeBadge.color.split(' ')[0]}`}>
                                <TypeIcon size={20} className={typeBadge.color.split(' ')[1]} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-medium text-stone-900">
                                    {registration.items?.[0]?.name || registration.registration_type}
                                  </h3>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeBadge.color}`}>
                                    {typeBadge.label}
                                  </span>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge.color}`}>
                                    {statusBadge.label}
                                  </span>
                                </div>
                                
                                {/* Workshop details if available */}
                                {registration.registration_data?.workshop_name && (
                                  <p className="text-sm text-stone-600 mt-1">
                                    {registration.registration_data.workshop_name}
                                  </p>
                                )}
                                
                                <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                                  <span className="flex items-center gap-1">
                                    <Clock size={14} />
                                    {formatDate(registration.created_at)}
                                  </span>
                                  {registration.registration_data?.payment_method && (
                                    <span className="capitalize">
                                      via {registration.registration_data.payment_method}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Right side - Amount */}
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-semibold text-cafa-red">
                                ${registration.total_amount?.toFixed(2) || '0.00'}
                              </p>
                              <p className="text-xs text-stone-500">
                                {registration.items?.length || 1} item{(registration.items?.length || 1) > 1 ? 's' : ''}
                              </p>
                            </div>
                            <ChevronRight size={20} className="text-stone-300 hidden sm:block" />
                          </div>
                        </div>
                        
                        {/* Items breakdown (if multiple) */}
                        {registration.items && registration.items.length > 1 && (
                          <div className="mt-4 pt-4 border-t border-stone-100">
                            <p className="text-xs font-medium text-stone-500 mb-2">Items</p>
                            <div className="space-y-1">
                              {registration.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-stone-600">
                                    {item.name} {item.quantity > 1 && `(x${item.quantity})`}
                                  </span>
                                  <span className="text-stone-900">${item.price?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Notes if available */}
                        {registration.registration_data?.notes && (
                          <div className="mt-3 pt-3 border-t border-stone-100">
                            <p className="text-sm text-stone-600 italic">
                              "{registration.registration_data.notes}"
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Summary */}
              {filteredRegistrations.length > 0 && (
                <div className="mt-6 pt-6 border-t border-stone-200">
                  <div className="flex justify-between items-center">
                    <div className="text-stone-600">
                      <span className="font-medium">{filteredRegistrations.length}</span> registration{filteredRegistrations.length !== 1 ? 's' : ''} found
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-stone-500">Total Spent</p>
                      <p className="text-xl font-semibold text-cafa-red">
                        ${filteredRegistrations.reduce((sum, r) => sum + (r.total_amount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
